import React, { useEffect, useRef, useState } from 'react';
import { FiLoader } from 'react-icons/fi';

import { useFrameImage } from './useFrameImage';
import SegmentationOverlay from './SegmentationOverlay';
import type { QuickInferenceSegmentation } from '../../../helpers/api/quick-inference/quick-inference';
import { formatViewLabel } from './quickInferenceUtils';

interface Props {
  inferenceId: string;
  frameNumber: number;
  views?: string[];
  pendingCount?: number;
  segmentations: QuickInferenceSegmentation[];
  selected: boolean;
  refreshKey?: number;
  onClick: () => void;
}

export function QuickInferenceFrameThumb({
  inferenceId,
  frameNumber,
  views = [],
  pendingCount = 0,
  segmentations,
  selected,
  refreshKey = 0,
  onClick,
}: Props) {
  const ref = useRef<HTMLButtonElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '80px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const { url, loading, failed } = useFrameImage(
    visible ? inferenceId : '',
    frameNumber,
    refreshKey
  );
  const segCount = segmentations.length;
  const viewCount = views.length;

  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      className={`group w-[6.5rem] shrink-0 overflow-hidden rounded-lg border-2 text-left transition-all focus:outline-none ${
        selected
          ? 'border-blue-500 ring-2 ring-blue-200 dark:ring-blue-800'
          : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
      }`}
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-gray-900">
        {!visible ? (
          <div className="flex h-full items-center justify-center text-[10px] text-gray-500">…</div>
        ) : loading ? (
          <div className="flex h-full items-center justify-center">
            <FiLoader size={14} className="animate-spin text-blue-400" />
          </div>
        ) : failed || !url ? (
          <div className="flex h-full items-center justify-center text-[10px] text-gray-500">N/A</div>
        ) : (
          <SegmentationOverlay imageUrl={url} segmentations={segmentations} showLabels={false} />
        )}
        <span className="absolute left-1 top-1 rounded bg-black/60 px-1 text-[8px] font-bold text-white">
          {frameNumber + 1}
        </span>
        {selected ? (
          <div className="absolute inset-0 flex items-end justify-center bg-blue-500/20 pb-1">
            <span className="rounded-full bg-blue-500 px-1.5 py-0.5 text-[8px] font-bold text-white">
              Active
            </span>
          </div>
        ) : (
          <div
            className="absolute inset-0 flex items-end justify-center pb-1 opacity-0 transition-opacity group-hover:opacity-100"
            style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.55), transparent)' }}
          >
            <span className="text-[8px] font-semibold text-white">Open</span>
          </div>
        )}
      </div>
      <div className="space-y-0.5 bg-white px-2 py-1.5 dark:bg-gray-800">
        <div className="text-[10px] font-medium text-gray-700 dark:text-gray-200">
          Frame {frameNumber}
        </div>
        {viewCount > 0 ? (
          <div className="truncate text-[9px] text-violet-600 dark:text-violet-400">
            {formatViewLabel(views[0])}
          </div>
        ) : null}
        <div className="text-[9px] text-gray-400">
          {segCount > 0
            ? `${segCount} structure${segCount === 1 ? '' : 's'}`
            : pendingCount > 0
              ? 'Assigning…'
              : viewCount > 0
                ? 'View detected'
                : 'Empty'}
        </div>
      </div>
    </button>
  );
}
